import {
  Box,
  Button,
  Card,
  Group,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  SimpleGrid,
  Table,
  Pagination,
  Select,
  useMantineTheme,
  useMantineColorScheme,
} from "@mantine/core";
import { ArrowsClockwiseIcon } from "@phosphor-icons/react";
import {
  useEncounters,
  useObservations,
  useConditions,
  useMedicationRequests,
  usePractitioners,
  useMedicationAdministrations,
  useProcedures,
  useServiceRequests,
  useMe,
  useSyncHealthData,
} from "@/api";
import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/** Format ISO date-time for table display */
const formatDateTime = (value: string | null | undefined) => {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  return d.toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" });
};

/** Format date only */
const formatDate = (value: string | null | undefined) => {
  if (value == null || value === "") return "—";
  return parseLocalDate(value).toLocaleDateString("en-US");
};

/** Compute age in years from date of birth (YYYY-MM-DD or ISO string). Returns "—" if invalid/missing. */
// "1955-07-08" must not shift to the previous day in US time zones: parse date-only
// strings as local dates (new Date("YYYY-MM-DD") is UTC midnight).
const parseLocalDate = (value: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
const ageFromDateOfBirth = (dob: string | null | undefined): string => {
  if (dob == null || dob === "") return "—";
  const birth = parseLocalDate(dob);
  if (Number.isNaN(birth.getTime())) return "—";
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return String(age);
};

/** Full display name from first, middle, last. */
const fullName = (first?: string | null, middle?: string | null, last?: string | null): string => {
  const parts = [first, middle, last].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

/** Observation component shape (e.g. blood pressure panel: systolic/diastolic). */
type ObservationComponent = { code?: string; unit?: string; value?: number | string; display?: string };

/** Render observation value/unit: single value or panel components (e.g. blood pressure). */
const ObservationValueCell = ({
  row,
  textColor,
}: {
  row: { value?: string; unit?: string; components?: ObservationComponent[] };
  textColor: string;
}) => {
  const components = Array.isArray(row.components) ? row.components : [];
  if (components.length > 0) {
    return (
      <Stack gap={2}>
        {components.map((c, i) => (
          <Text key={i} size="sm" c={textColor}>
            {c.display ?? c.code ?? "—"}: {c.value != null ? String(c.value) : "—"} {c.unit ?? ""}
          </Text>
        ))}
      </Stack>
    );
  }
  const val = row.value ?? "—";
  const unit = row.unit ?? "";
  return <Text size="sm" c={textColor}>{val} {unit ? ` ${unit}` : ""}</Text>;
};

/** Patient Demographics card: displays current user data from Users Me API (/api/users/me/). */
const PatientDemographicsCard = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const { data: user, isLoading } = useMe();

  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const name = user ? fullName(user.first_name, user.middle_name, user.last_name) : "—";
  const age = user ? ageFromDateOfBirth(user.date_of_birth) : "—";
  const dob = user ? formatDate(user.date_of_birth) : "—";
  const gender = user?.gender != null ? String(user.gender) : "—";
  const email = user?.email ?? "—";
  const phone = user?.phone_number ?? "—";

  return (
    <Card withBorder radius="md" p={0} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Patient Demographics
      </Text>
      <Box p="md" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Card withBorder p="sm" bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[1]} style={{ flex: 1 }}>
          {isLoading ? (
            <Text size="sm" c="dimmed">Loading…</Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md" verticalSpacing="md">
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Name</Text>
                <Text fw={500} c={textColor} size="sm">{name}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Age</Text>
                <Text fw={500} c={textColor} size="sm">{age}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Date of Birth</Text>
                <Text fw={500} c={textColor} size="sm">{dob}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Gender</Text>
                <Text fw={500} c={textColor} size="sm">{gender}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Email</Text>
                <Text fw={500} c={textColor} size="sm" style={{ wordBreak: "break-word" }}>{email}</Text>
              </Box>
              <Box>
                <Text size="xs" c="dimmed" mb={4}>Phone</Text>
                <Text fw={500} c={textColor} size="sm">{phone}</Text>
              </Box>
            </SimpleGrid>
          )}
        </Card>
      </Box>
    </Card>
  );
};

const DataCompletenessCard = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const completeness = 75;
  const radius = 80;
  const strokeWidth = 16;
  const centerY = 100;
  const circumference = Math.PI * radius;
  const offset = circumference - (completeness / 100) * circumference;

  return (
    <Card withBorder radius="md" p="md" bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[0]} style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Stack align="center" gap="lg" style={{ flex: 1, justifyContent: "center" }}>
        {/* Gauge Section - Upper Half */}
        <Box style={{ position: "relative", width: "240px", height: "140px", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
          <svg
            width="240"
            height="140"
            viewBox="0 0 240 140"
            style={{ overflow: "visible" }}
          >
            <defs>
              <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1e3a8a" />
                <stop offset="25%" stopColor="#4f46e5" />
                <stop offset="50%" stopColor="#7c3aed" />
                <stop offset="75%" stopColor="#a855f7" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            {/* Background arc (semi-circle) */}
            <path
              d={`M 30 ${centerY} A ${radius} ${radius} 0 0 1 210 ${centerY}`}
              fill="none"
              stroke={colorScheme === "dark" ? theme.colors.neutral[7] : theme.colors.gray[2]}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            {/* Progress arc */}
            <path
              d={`M 30 ${centerY} A ${radius} ${radius} 0 0 1 210 ${centerY}`}
              fill="none"
              stroke="url(#gaugeGradient)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
            />
          </svg>
          <Box
            style={{
              position: "absolute",
              top: "70px",
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
            }}
          >
            <Text
              fw={700}
              size="2xl"
              c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
            >
              {completeness}%
            </Text>
          </Box>
        </Box>
        
        {/* Text Section - Lower Half */}
        <Stack align="center" gap="xs" mt="md">
          <Text
            fw={600}
            size="md"
            c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}
            ta="center"
          >
            Data Completeness
          </Text>
          <Text
            size="sm"
            c={colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6]}
            ta="center"
            style={{ maxWidth: "100%" }}
          >
            A {completeness}% completeness score means your profile includes most of your essential health details.
          </Text>
        </Stack>
      </Stack>
    </Card>
  );
};

/** Paginated table for Encounters (schema: Encounter). */
const EncountersTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useEncounters({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Healthcare Encounters
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Class</Table.Th>
                <Table.Th>Start</Table.Th>
                <Table.Th>End</Table.Th>
                <Table.Th>Reason</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6} c={mutedColor}>No encounters found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.status ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.class_display ?? row.class_code ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.start_datetime)}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.end_datetime)}</Table.Td>
                    <Table.Td c={textColor}>{row.reason_code_text ?? row.reason_code_display ?? "—"}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Observations (schema: Observation). */
const ObservationsTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useObservations({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Observations
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th>Display</Table.Th>
                <Table.Th>Effective</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Value / Unit</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6} c={mutedColor}>No observations found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.code ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.display ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.effective_datetime)}</Table.Td>
                    <Table.Td c={textColor}>{row.category ?? "—"}</Table.Td>
                    <Table.Td>
                      <ObservationValueCell row={row} textColor={textColor} />
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Medication Requests (schema: MedicationRequest). */
const MedicationRequestsTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useMedicationRequests({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Medication Requests
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Medication (display)</Table.Th>
                <Table.Th>Medication (text)</Table.Th>
                <Table.Th>Code</Table.Th>
                <Table.Th>Last updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6} c={mutedColor}>No medication requests found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.status ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.medication_display ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.medication_text ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.medication_code ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.last_updated)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Practitioners (schema: Practitioner). */
const PractitionersTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = usePractitioners({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  const fullName = (r: { first_name?: string; middle_name?: string; last_name?: string; suffix?: string }) =>
    [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ") + (r.suffix ? ` ${r.suffix}` : "") || "—";
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Healthcare Practitioners
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Gender</Table.Th>
                <Table.Th>Birth date</Table.Th>
                <Table.Th>Active</Table.Th>
                <Table.Th>Last updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6} c={mutedColor}>No practitioners found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{fullName(row)}</Table.Td>
                    <Table.Td c={textColor}>{row.gender ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDate(row.birth_date)}</Table.Td>
                    <Table.Td c={textColor}>{row.active == null ? "—" : row.active ? "Yes" : "No"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.last_updated)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Conditions (schema: Condition). */
const ConditionsTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useConditions({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Conditions & Health Concerns
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Condition name</Table.Th>
                <Table.Th>Condition code</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3} c={mutedColor}>No conditions found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.condition_name ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.condition_code ?? "—"}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Medication Administrations (schema: MedicationAdministration). */
const MedicationAdministrationsTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useMedicationAdministrations({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Medication Administration
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Medication (display)</Table.Th>
                <Table.Th>Medication (text)</Table.Th>
                <Table.Th>Effective</Table.Th>
                <Table.Th>Dosage</Table.Th>
                <Table.Th>Last updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7} c={mutedColor}>No medication administrations found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.status ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.medication_display ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.medication_text ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.effective_datetime)}</Table.Td>
                    <Table.Td c={textColor}>{row.dosage_text ?? (row.dosage_value && row.dosage_unit ? `${row.dosage_value} ${row.dosage_unit}` : "—")}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.last_updated)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Procedures (schema: Procedure). */
const ProceduresTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useProcedures({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Procedure
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Category</Table.Th>
                <Table.Th>Code (display)</Table.Th>
                <Table.Th>Code (text)</Table.Th>
                <Table.Th>Performed</Table.Th>
                <Table.Th>Last updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7} c={mutedColor}>No procedures found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.status ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.category_display ?? row.category_text ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.code_display ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.code_text ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.performed_datetime)}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.last_updated)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Paginated table for Service Requests (schema: ServiceRequest). */
const ServiceRequestsTable = () => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");
  const size = parseInt(pageSize, 10);
  const { data } = useServiceRequests({ page, page_size: size });
  const results = data?.results ?? [];
  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / size));
  useEffect(() => setPage(1), [pageSize]);
  const textColor = colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9];
  const mutedColor = colorScheme === "dark" ? theme.colors.neutral[4] : theme.colors.neutral[6];
  return (
    <Card withBorder radius="md" p={0}>
      <Text fw={500} bg={colorScheme === "dark" ? theme.colors.neutral[9] : theme.colors.neutral[3]} p="sm" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.neutral[9]}>
        Service Request
      </Text>
      <Box p="md">
        <ScrollArea>
          <Table withTableBorder withColumnBorders striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>External ID</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Intent</Table.Th>
                <Table.Th>Code (display)</Table.Th>
                <Table.Th>Code (text)</Table.Th>
                <Table.Th>Authored on</Table.Th>
                <Table.Th>Last updated</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {results.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={7} c={mutedColor}>No service requests found.</Table.Td>
                </Table.Tr>
              ) : (
                results.map((row) => (
                  <Table.Tr key={row.id}>
                    <Table.Td c={textColor}>{row.external_id ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.status ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.intent ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.code_display ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{row.code_text ?? "—"}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.authored_on)}</Table.Td>
                    <Table.Td c={textColor}>{formatDateTime(row.last_updated)}</Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
        <Group justify="space-between" mt="sm">
          <Group gap="xs">
            <Text size="sm" c={mutedColor}>Rows per page:</Text>
            <Select size="xs" w={70} data={["5", "10", "25", "50"]} value={pageSize} onChange={(v) => v && setPageSize(v)} />
            <Text size="sm" c={mutedColor}>{results.length ? `${(page - 1) * size + 1}-${(page - 1) * size + results.length}` : "0"}-{count} of {count}</Text>
          </Group>
          <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
        </Group>
      </Box>
    </Card>
  );
};

/** Resource type tab value; must match Tabs value and panel rendering. */
const RESOURCE_TABS = [
  { value: "encounters", label: "Encounters" },
  { value: "observations", label: "Observations" },
  { value: "medication-requests", label: "Medication Requests" },
  { value: "practitioners", label: "Practitioners" },
  { value: "conditions", label: "Conditions" },
  { value: "medication-administrations", label: "Medication Administration" },
  { value: "procedures", label: "Procedure" },
  { value: "service-requests", label: "Service Request" },
] as const;

/** Query key prefixes used to invalidate health data after sync (refetch all list + me). */
const HEALTH_DATA_QUERY_KEY_PREFIXES = [
  { url: "/api/users/me/" },
  { url: "/api/encounters/" },
  { url: "/api/observations/" },
  { url: "/api/conditions/" },
  { url: "/api/medication-requests/" },
  { url: "/api/practitioners/" },
  { url: "/api/medication-administrations/" },
  { url: "/api/procedures/" },
  { url: "/api/service-requests/" },
] as const;

const HealthSummarySection = () => {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const { refetch: syncHealthData, isFetching: isSyncing } = useSyncHealthData({
    query: { enabled: false },
  });

  const handleSync = async () => {
    await syncHealthData();
    await Promise.all(
      HEALTH_DATA_QUERY_KEY_PREFIXES.map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    );
  };

  return (
    <Box p="xs" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Group justify="space-between" mb="sm" style={{ flexShrink: 0 }}>
        <Text size="lg" c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.indigo[9]} fw={500}>
          Health Summary
        </Text>
        <Button
          variant="outline"
          leftSection={<ArrowsClockwiseIcon size={16} />}
          c={colorScheme === "dark" ? theme.colors.neutral[0] : theme.colors.indigo[9]}
          fw={500}
          loading={isSyncing}
          onClick={handleSync}
        >
          Sync
        </Button>
      </Group>

      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
          <Box style={{ gridColumn: "span 2", minHeight: 0, display: "flex" }}>
            <Box style={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <PatientDemographicsCard />
            </Box>
          </Box>
          <Box style={{ minHeight: 0, display: "flex" }}>
            <Box style={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <DataCompletenessCard />
            </Box>
          </Box>
        </SimpleGrid>

        <Tabs defaultValue="encounters" keepMounted={false}>
          <ScrollArea scrollbarSize={6} type="hover">
            <Tabs.List style={{ flexWrap: "nowrap", marginBottom: 8 }}>
              {RESOURCE_TABS.map((tab) => (
                <Tabs.Tab key={tab.value} value={tab.value}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </ScrollArea>
          {RESOURCE_TABS.map((tab) => (
            <Tabs.Panel key={tab.value} value={tab.value}>
              {tab.value === "encounters" && <EncountersTable />}
              {tab.value === "observations" && <ObservationsTable />}
              {tab.value === "medication-requests" && <MedicationRequestsTable />}
              {tab.value === "practitioners" && <PractitionersTable />}
              {tab.value === "conditions" && <ConditionsTable />}
              {tab.value === "medication-administrations" && <MedicationAdministrationsTable />}
              {tab.value === "procedures" && <ProceduresTable />}
              {tab.value === "service-requests" && <ServiceRequestsTable />}
            </Tabs.Panel>
          ))}
        </Tabs>
      </Stack>
    </Box>
  );
};

export default HealthSummarySection;
